// components/senders/SenderTable.jsx
import React from "react";
import { motion } from "framer-motion";
import {
  Edit3,
  Trash2,
  Copy,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from "lucide-react";
import moment from "moment";

const SenderTable = ({
  senderIds,
  currentPage,
  totalPages,
  startIndex,
  endIndex,
  itemsPerPage,
  handlePageChange,
  editingId,
  editValue,
  setEditValue,
  errors,
  setErrors,
  handleEditSenderId,
  cancelEditing,
  startEditing,
  copyToClipboard,
  openDeleteModal,
  getStatusColor,
  pagination,
  handlePerPageChange,
}) => {
  if (senderIds.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No Sender IDs found
        </h3>
        <p className="text-gray-600">
          {pagination?.searchQuery
            ? "No results match your search criteria"
            : "Create your first sender ID to get started"}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                Sender ID
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                Created Date
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {senderIds.map((sender, index) => {
              // Use sender_slug if available, otherwise use id
              const senderIdentifier = sender.sender_slug || sender.id;
              const isEditing = editingId === senderIdentifier;
              const canEdit =
                sender.status?.toLowerCase() === "approved" ||
                sender.status?.toLowerCase() === "pending";
              const canDelete = sender.status?.toLowerCase() !== "rejected";

              return (
                <motion.tr
                  key={sender.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                  className="hover:bg-gray-50 transition-colors duration-150"
                >
                  <td className="px-6 py-4">
                    {isEditing ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => {
                            setEditValue(e.target.value);
                            if (Object.keys(errors).length > 0) {
                              setErrors({});
                            }
                          }}
                          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          maxLength={11}
                          placeholder="Enter sender ID"
                          autoFocus
                        />
                        {Object.keys(errors).length > 0 && (
                          <div className="space-y-1">
                            {Object.values(errors).map((error, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-1.5 p-2 text-xs text-red-600 rounded bg-red-50"
                              >
                                <AlertCircle className="flex-shrink-0 w-3.5 h-3.5" />
                                <span>{error}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {sender.sender}
                        </span>
                        <button
                          onClick={() => copyToClipboard(sender.sender)}
                          className="p-1 text-gray-400 hover:text-purple-600"
                          title="Copy to clipboard"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        sender.status
                      )}`}
                    >
                      {sender.status?.charAt(0).toUpperCase() +
                        sender.status?.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {moment(sender.created_at).format("MMM DD, YYYY")}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2 items-center">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => handleEditSenderId(senderIdentifier)}
                            disabled={!editValue.trim() || editValue === sender.sender}
                            className={`p-1.5 rounded ${
                              !editValue.trim() || editValue === sender.sender
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-green-600 hover:bg-green-50"
                            }`}
                            title={editValue === sender.sender ? "No changes made" : "Save"}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="p-1.5 text-red-600 rounded hover:bg-red-50"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => canEdit && startEditing(sender)}
                            disabled={!canEdit}
                            className={`p-1.5 rounded ${
                              canEdit
                                ? "text-blue-600 hover:bg-blue-50"
                                : "text-gray-300 cursor-not-allowed"
                            }`}
                            title={
                              canEdit
                                ? "Edit"
                                : "Only approved and pending senders can be edited"
                            }
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => canDelete && openDeleteModal(sender)}
                            disabled={!canDelete}
                            className={`p-1.5 rounded ${
                              canDelete
                                ? "text-gray-400 hover:text-red-600 hover:bg-red-50"
                                : "text-gray-300 cursor-not-allowed"
                            }`}
                            title={
                              canDelete
                                ? "Delete"
                                : "Rejected senders cannot be deleted"
                            }
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Enhanced Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-col gap-4 justify-between items-center sm:flex-row">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-700">Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) =>
                  handlePerPageChange && handlePerPageChange(Number(e.target.value))
                }
                className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value={9}>9</option>
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="text-sm text-gray-700">entries</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Previous</span>
              </button>

              <div className="flex gap-1 items-center">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (page) =>
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 2 && page <= currentPage + 2)
                  )
                  .map((page, index, array) => {
                    if (index > 0 && page - array[index - 1] > 1) {
                      return (
                        <span key={`ellipsis-${page}`} className="px-2">
                          ...
                        </span>
                      );
                    }
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          currentPage === page
                            ? "bg-purple-600 text-white"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="hidden sm:inline mr-1">Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="text-sm text-gray-700">
              Showing {startIndex} to {endIndex} of {pagination?.total || senderIds.length} entries
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SenderTable;